'use client'
import * as React from 'react'
import { ArrowDown, ArrowUp, Plus, RotateCcw, Trash2, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// Attribution model comparator. A pure-browser worksheet: build an ordered
// customer journey, then see how four attribution models split the conversion
// value across the channels. The honest point the tool surfaces is computed
// live from the user's own path: last-click hands all credit to the final
// touch, so a top-of-funnel UGC / social discovery touch can show as a zero
// while first-click and linear credit it properly.
//
// Mirrors attributionCreditFor()/ATTRIBUTION_MODELS in
// nextapp/lib/publicToolsMath.ts (nextapp's white-label copy for Feefo, see
// this repo's CLAUDE.md "Public calculator tools").

type Touchpoint = { id: string; channel: string }

type ModelKey = 'first' | 'last' | 'linear' | 'position'

const MODELS: { key: ModelKey; label: string; blurb: string }[] = [
  { key: 'first', label: 'First-click', blurb: '100% to the touch that started the journey' },
  { key: 'last', label: 'Last-click', blurb: '100% to the final touch before purchase' },
  { key: 'linear', label: 'Linear', blurb: 'Even split across every touch' },
  { key: 'position', label: 'Position-based', blurb: '40% first, 40% last, 20% shared by the middle' },
]

const DEFAULT_PATH: Touchpoint[] = [
  { id: 'tp-1', channel: 'Instagram UGC' },
  { id: 'tp-2', channel: 'Google Search' },
  { id: 'tp-3', channel: 'Email' },
  { id: 'tp-4', channel: 'Direct' },
]

let idCounter = 100
function nextId() {
  idCounter += 1
  return `tp-${idCounter}`
}

// Returns the credit assigned to each touch index (parallel to the path array)
// under a given model. Sum of every returned array equals the conversion value.
function creditFor(model: ModelKey, n: number, value: number): number[] {
  if (n === 0) return []
  if (n === 1) return [value]
  const out = new Array<number>(n).fill(0)
  if (model === 'first') {
    out[0] = value
    return out
  }
  if (model === 'last') {
    out[n - 1] = value
    return out
  }
  if (model === 'linear') {
    return out.map(() => value / n)
  }
  // position-based / U-shaped
  if (n === 2) {
    return [value * 0.5, value * 0.5]
  }
  const middle = n - 2
  const middleShare = (value * 0.2) / middle
  out[0] = value * 0.4
  out[n - 1] = value * 0.4
  for (let i = 1; i < n - 1; i += 1) out[i] = middleShare
  return out
}

// Aggregate per-channel credit (a channel name can appear in more than one
// touch, so we sum by name) for a single model.
function channelTotals(path: Touchpoint[], model: ModelKey, value: number): Map<string, number> {
  const credit = creditFor(model, path.length, value)
  const totals = new Map<string, number>()
  path.forEach((tp, i) => {
    const name = tp.channel.trim() || 'Untitled'
    totals.set(name, (totals.get(name) ?? 0) + credit[i])
  })
  return totals
}

export function AttributionComparator() {
  const [path, setPath] = React.useState<Touchpoint[]>(DEFAULT_PATH)
  const [value, setValue] = React.useState<number>(120)

  const safeValue = value > 0 ? value : 0

  // Ordered, de-duplicated list of channel names for table rows.
  const channelOrder = React.useMemo(() => {
    const seen: string[] = []
    for (const tp of path) {
      const name = tp.channel.trim() || 'Untitled'
      if (!seen.includes(name)) seen.push(name)
    }
    return seen
  }, [path])

  // Per-model, per-channel credit table.
  const table = React.useMemo(() => {
    const byModel = {} as Record<ModelKey, Map<string, number>>
    for (const m of MODELS) byModel[m.key] = channelTotals(path, m.key, safeValue)
    return byModel
  }, [path, safeValue])

  // Headline insight: the first touch (top-of-funnel discovery, often UGC /
  // social) earns full credit under first-click but nothing under last-click,
  // unless it also happens to be the last touch.
  const insight = React.useMemo(() => {
    if (path.length < 2 || safeValue <= 0) return null
    const firstName = (path[0].channel.trim() || 'Untitled')
    const lastName = (path[path.length - 1].channel.trim() || 'Untitled')
    const firstUnderFirst = table.first.get(firstName) ?? 0
    const firstUnderLast = table.last.get(firstName) ?? 0
    const firstUnderLinear = table.linear.get(firstName) ?? 0
    const lost = firstUnderFirst - firstUnderLast
    const isTopFunnelBlind = firstName !== lastName && firstUnderLast === 0
    return {
      firstName,
      lastName,
      firstUnderFirst,
      firstUnderLast,
      firstUnderLinear,
      lost,
      isTopFunnelBlind,
    }
  }, [path, safeValue, table])

  function updateChannel(id: string, channel: string) {
    setPath((p) => p.map((tp) => (tp.id === id ? { ...tp, channel } : tp)))
  }

  function addTouch() {
    setPath((p) => [...p, { id: nextId(), channel: 'New touch' }])
  }

  function removeTouch(id: string) {
    setPath((p) => (p.length > 1 ? p.filter((tp) => tp.id !== id) : p))
  }

  function move(index: number, dir: -1 | 1) {
    setPath((p) => {
      const target = index + dir
      if (target < 0 || target >= p.length) return p
      const copy = p.slice()
      const tmp = copy[index]
      copy[index] = copy[target]
      copy[target] = tmp
      return copy
    })
  }

  function reset() {
    setPath(DEFAULT_PATH.map((tp) => ({ ...tp })))
    setValue(120)
  }

  const money = (n: number) =>
    '$' + n.toLocaleString('en-US', { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })
  const pct = (n: number) => (safeValue > 0 ? Math.round((n / safeValue) * 100) : 0)

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,380px)_1fr]">
      {/* Builder column */}
      <div className="rounded-2xl border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b p-5 sm:p-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Your journey</p>
            <h2 className="mt-1 font-display text-xl font-medium tracking-tight">Build the purchase path</h2>
          </div>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <RotateCcw className="h-3 w-3" aria-hidden /> Reset
          </button>
        </div>

        <div className="space-y-5 p-5 sm:p-6">
          <label className="block text-sm">
            <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Conversion value</span>
            <span className="mt-0.5 block text-[11px] text-muted-foreground">The order value being credited across the path</span>
            <div className="relative mt-1.5">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
              <Input
                type="number"
                inputMode="decimal"
                min={0}
                step={10}
                value={value}
                onChange={(e) => setValue(Number(e.target.value) || 0)}
                className="pl-7 tabular-nums"
                aria-label="Conversion value in dollars"
              />
            </div>
          </label>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Touchpoints, in order</span>
              <span className="text-[11px] text-muted-foreground tabular-nums">{path.length} touches</span>
            </div>
            <ol className="mt-2 space-y-2">
              {path.map((tp, i) => (
                <li key={tp.id} className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary text-xs font-semibold tabular-nums text-muted-foreground"
                  >
                    {i + 1}
                  </span>
                  <Input
                    value={tp.channel}
                    onChange={(e) => updateChannel(tp.id, e.target.value)}
                    aria-label={`Channel name for touch ${i + 1}`}
                    className="flex-1"
                  />
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      aria-label={`Move touch ${i + 1} earlier`}
                      className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-30"
                    >
                      <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(i, 1)}
                      disabled={i === path.length - 1}
                      aria-label={`Move touch ${i + 1} later`}
                      className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-30"
                    >
                      <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeTouch(tp.id)}
                      disabled={path.length <= 1}
                      aria-label={`Remove touch ${i + 1}`}
                      className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-30"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                </li>
              ))}
            </ol>
            <button
              type="button"
              onClick={addTouch}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden /> Add touchpoint
            </button>
          </div>

          <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
            Everything runs in your browser. No inputs are logged or stored.
          </p>
        </div>
      </div>

      {/* Results column */}
      <div className="space-y-6">
        {insight && (
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 sm:p-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">What last-click hides</p>
            {insight.isTopFunnelBlind ? (
              <p className="mt-2 text-pretty text-base leading-relaxed text-foreground">
                <span className="font-semibold">{insight.firstName}</span> kicked off this purchase, yet last-click
                gives it <span className="font-semibold tabular-nums">{money(0)}</span>. First-click credits it the full{' '}
                <span className="font-semibold tabular-nums">{money(insight.firstUnderFirst)}</span>, and linear still
                hands it <span className="font-semibold tabular-nums">{money(insight.firstUnderLinear)}</span>. The
                discovery touch did real work that last-click reporting never sees.
              </p>
            ) : insight.lost > 0 ? (
              <p className="mt-2 text-pretty text-base leading-relaxed text-foreground">
                <span className="font-semibold">{insight.firstName}</span> opened the journey. Last-click credits it{' '}
                <span className="font-semibold tabular-nums">{money(insight.firstUnderLast)}</span>, versus{' '}
                <span className="font-semibold tabular-nums">{money(insight.firstUnderFirst)}</span> under first-click:
                a <span className="font-semibold tabular-nums">{money(insight.lost)}</span> gap in how the opening touch
                gets valued.
              </p>
            ) : (
              <p className="mt-2 text-pretty text-base leading-relaxed text-foreground">
                Here the opening and closing touch line up, so the models broadly agree. Add an early discovery touch
                (say Instagram UGC) to see how much credit last-click would strip from it.
              </p>
            )}
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
          <div className="border-b p-5 sm:p-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Credit by model</p>
            <h2 className="mt-1 font-display text-xl font-medium tracking-tight">
              How each model splits {money(safeValue)}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Every column sums to the conversion value. Bars show each channel&rsquo;s share of that model.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">
                Attribution credit assigned to each channel under first-click, last-click, linear and position-based models
              </caption>
              <thead>
                <tr className="border-b">
                  <th scope="col" className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Channel
                  </th>
                  {MODELS.map((m) => (
                    <th
                      key={m.key}
                      scope="col"
                      className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                      {m.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {channelOrder.map((name) => (
                  <tr key={name} className="border-b last:border-b-0 align-top">
                    <th scope="row" className="px-4 py-4 text-left font-medium">
                      {name}
                    </th>
                    {MODELS.map((m) => {
                      const credit = table[m.key].get(name) ?? 0
                      const share = pct(credit)
                      const isZero = credit <= 0
                      return (
                        <td key={m.key} className="px-4 py-4">
                          <div className={cn('font-medium tabular-nums', isZero && 'text-muted-foreground')}>
                            {money(credit)}
                          </div>
                          <div className="mt-1.5 flex items-center gap-2">
                            <div
                              className="h-1.5 w-full max-w-[120px] overflow-hidden rounded-full bg-secondary"
                              role="img"
                              aria-label={`${share} percent under ${m.label}`}
                            >
                              <div
                                className="bar-fill h-full rounded-full bg-primary"
                                style={{ width: `${share}%` }}
                              />
                            </div>
                            <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{share}%</span>
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid gap-px border-t bg-border sm:grid-cols-2 lg:grid-cols-4">
            {MODELS.map((m) => (
              <div key={m.key} className="bg-card p-4">
                <div className="text-xs font-semibold">{m.label}</div>
                <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{m.blurb}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3 rounded-2xl border bg-secondary/30 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <p className="text-sm text-muted-foreground">
            Tag products inside the UGC and video that drives discovery, then track its real path to purchase.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm">
              <a href="https://app.idukki.io/register" target="_blank" rel="noopener noreferrer">
                Get started
              </a>
            </Button>
            <Button asChild size="sm" variant="outline">
              <a href="/contact?intent=attribution">Talk to us</a>
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        .bar-fill {
          transition: width var(--dur, 260ms) var(--ease-spring, cubic-bezier(0.22, 1, 0.36, 1));
        }
        @media (prefers-reduced-motion: reduce) {
          .bar-fill { transition: none; }
        }
      `}</style>
    </div>
  )
}
